import type {CodeNavigationInfo, CodeSymbol} from '@github-ui/code-nav'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import {SymbolChangedEvent} from '@github-ui/copilot-chat-events'
import type {CopilotChatReference} from '@github-ui/copilot-chat-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {qualifyRef, type RefType} from '@github-ui/ref-utils'
import {startTransition, useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'

import {useCurrentBlob} from '../../../../hooks/CurrentBlob'
import {useFocusSymbolPane} from '../../../../hooks/use-focus-symbol-pane'
import {CodeNavSymbolDetails} from './CodeNavSymbolDetails'
import {CodeNavSymbolNavigation} from './CodeNavSymbolNavigation'

export function CodeNavInfoPanel({
  selectedText,
  showCodeNavWithSymbol,
  lineNumber,
  offset,
  onClose,
  onClear,
  codeNavInfo,
  isLoading,
  autoFocusSearch,
}: {
  selectedText: string
  lineNumber: number
  offset: number
  onClose: () => void
  onClear: () => void
  showCodeNavWithSymbol: (symbolData: CodeSymbol) => void
  codeNavInfo: CodeNavigationInfo
  isLoading: boolean
  autoFocusSearch: boolean
}) {
  // Stable reference for showCodeNavWithSymbol to prevent callback recreation
  const showCodeNavWithSymbolRef = useRef(showCodeNavWithSymbol)
  useEffect(() => {
    showCodeNavWithSymbolRef.current = showCodeNavWithSymbol
  }, [showCodeNavWithSymbol])

  const [showSymbolTree, setShowSymbolTree] = useState(selectedText ? false : true)

  const onSymbolSelect = useCallback((sym: CodeSymbol) => {
    showCodeNavWithSymbolRef.current(sym)
    setShowSymbolTree(false)
  }, [])

  useFocusSymbolPane(focusSymbolSearch => {
    if (focusSymbolSearch) {
      onClear()
      startTransition(() => setShowSymbolTree(true))
    }
  })

  const path = usePath()
  const refInfo = useRefInfo()
  const repo = useCurrentRepository()
  const {language, languageID, displayUrl} = useCurrentBlob()
  const fileReference: CopilotChatReference = useMemo(
    () => ({
      type: 'file',
      languageID,
      languageName: language,
      path,
      ref: qualifyRef(refInfo.name, refInfo.refType as RefType),
      commitOID: refInfo.currentOid,
      repoID: repo.id,
      repoName: repo.name,
      repoOwner: repo.ownerLogin,
      url: displayUrl,
    }),
    [
      languageID,
      language,
      path,
      refInfo.name,
      refInfo.refType,
      refInfo.currentOid,
      repo.id,
      repo.name,
      repo.ownerLogin,
      displayUrl,
    ],
  )

  // Derive the visible panel from the current selection during render instead of syncing it in an
  // effect: a non-empty selection shows symbol details, while clearing it returns to the symbol tree.
  // Only `selectedText` drives this decision, so we track it alone — depending on `codeNavInfo`
  // identity would add needless re-runs and risk a render loop if its memoization were ever removed.
  const [prevSelectedText, setPrevSelectedText] = useState(selectedText)
  if (prevSelectedText !== selectedText) {
    setPrevSelectedText(selectedText)
    if (selectedText) {
      setShowSymbolTree(false)
    } else if (!showSymbolTree) {
      setShowSymbolTree(true)
    }
  }

  // Notify Copilot chat when the selection clears back to file scope. The reactive reads live inside
  // a `useEffectEvent` so this imperative dispatch isn't treated as a reactive effect by the compiler.
  const prevSelectedTextRef = useRef(selectedText)
  const notifyReturnedToFileScope = useEffectEvent(() => {
    if (prevSelectedTextRef.current && !selectedText) {
      window.dispatchEvent(new SymbolChangedEvent(fileReference))
    }
    prevSelectedTextRef.current = selectedText
  })
  useEffect(() => {
    notifyReturnedToFileScope()
  }, [selectedText])

  return (
    <ErrorBoundary boundaryName="code-nav-info-panel" fallback={null}>
      <div id="symbols-pane">
        {showSymbolTree ? (
          codeNavInfo ? (
            <CodeNavSymbolNavigation
              treeSymbols={codeNavInfo.symbolTree}
              onSymbolSelect={onSymbolSelect}
              codeSymbols={codeNavInfo.symbols}
              onClose={onClose}
              autoFocusSearch={autoFocusSearch}
            />
          ) : (
            // TODO: Add zero state for when there is no code nav
            <div>Click on a symbol to see code navigation data</div>
          )
        ) : (
          <CodeNavSymbolDetails
            codeNavInfo={codeNavInfo}
            selectedText={selectedText}
            lineNumber={lineNumber}
            offset={offset}
            onBackToSymbol={() => {
              onClear()
              startTransition(() => setShowSymbolTree(true))
              window.dispatchEvent(new SymbolChangedEvent(fileReference))
            }}
            onClose={() => {
              onClose()
              onClear()
              window.dispatchEvent(new SymbolChangedEvent(fileReference))
            }}
            onSymbolSelect={onSymbolSelect}
            isLoading={isLoading}
          />
        )}
      </div>
    </ErrorBoundary>
  )
}
